import { useState, useEffect } from "react";
import api from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { Link } from "react-router-dom";

const statusColors = { healthy: "#22C55E", warning: "#F59E0B", critical: "#EF4444" };

export default function HostMapPage() {
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/pipelines").then((r) => { setPipelines(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);

  if (loading) return <div className="p-6" data-testid="hostmap-loading"><Skeleton className="h-8 w-48 bg-white/5" /></div>;

  const healthy = pipelines.filter((p) => p.status === "healthy").length;
  const warn = pipelines.filter((p) => p.status === "warning").length;
  const crit = pipelines.filter((p) => p.status === "critical").length;

  return (
    <div className="p-6 space-y-6" data-testid="hostmap-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Host Map</h1>
          <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>Visual pipeline health overview</p>
        </div>
        <div className="flex gap-3">
          {[{ l: "Healthy", c: healthy, col: "#22C55E" }, { l: "Warning", c: warn, col: "#F59E0B" }, { l: "Critical", c: crit, col: "#EF4444" }].map((s) => (
            <div key={s.l} className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: `${s.col}08`, border: `1px solid ${s.col}20` }}>
              <div className="w-2.5 h-2.5 rounded" style={{ background: s.col }} />
              <span className="text-[12px] font-semibold" style={{ color: s.col }}>{s.c}</span>
              <span className="text-[10px]" style={{ color: "#6B7280" }}>{s.l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Hex grid visualization */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="hostmap-grid">
        {pipelines.map((p, i) => {
          const color = statusColors[p.status] || "#5A6B82";
          const slaBreached = p.freshness_lag_minutes > p.sla_minutes;
          return (
            <Link to={`/pipelines/${p.pipeline_id}`} key={p.pipeline_id} className="sf-card p-4 animate-fade-up group relative overflow-hidden" style={{ animationDelay: `${i * 50}ms` }} data-testid={`hostmap-cell-${p.pipeline_id}`}>
              {/* Status indicator bar */}
              <div className="absolute top-0 left-0 right-0 h-1" style={{ background: color }} />
              {/* Glow effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `radial-gradient(circle at 50% 50%, ${color}08, transparent 70%)` }} />

              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}12` }}>
                    <span className="text-[14px] font-extrabold" style={{ color }}>{p.name.charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="w-3 h-3 rounded-full" style={{ background: color, boxShadow: `0 0 8px ${color}60` }} />
                </div>
                <h3 className="text-[12px] font-bold text-[#2D3142] font-mono truncate mb-1 group-hover:text-[#29B5E8] transition-colors">{p.name}</h3>
                <p className="text-[10px] font-mono truncate mb-3" style={{ color: "#6B7280" }}>{p.source_system}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[8px] uppercase tracking-widest font-semibold" style={{ color: "#9CA3AF" }}>Lag</div>
                    <div className={`text-[13px] font-extrabold font-mono ${slaBreached ? "text-red-400" : "text-[#2D3142]"}`}>{p.freshness_lag_minutes}m</div>
                  </div>
                  <div>
                    <div className="text-[8px] uppercase tracking-widest font-semibold" style={{ color: "#9CA3AF" }}>Rows</div>
                    <div className="text-[13px] font-extrabold font-mono text-[#2D3142]">{(p.last_row_count || 0).toLocaleString().slice(0, 6)}</div>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
